import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  HeartPulse,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SFDA_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <HeartPulse size={18} className="text-amber-400" />
            <span>Saudi Regulatory Compliance</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400">
            SFDA Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Saudi Food and Drug Authority - Ensuring Safety and Compliance for
            the Saudi Arabian Market
          </p>
        </motion.div>
      </section>

      {/* What is SFDA */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is SFDA Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              SFDA certification confirms that an organization's product or
              facility complies with the Saudi Food and Drug Authority
              regulations, ensuring product safety, quality, and compliance with
              national standards.
            </p>
            <p>
              The Saudi Food and Drug Authority (SFDA) is the regulatory body
              responsible for ensuring the safety, efficacy, and quality of
              food, drugs, medical devices, and cosmetics in Saudi Arabia. SFDA
              certification is a key requirement for gaining access to Saudi
              Arabian markets.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of SFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Regulatory Compliance",
                desc: "Ensures products meet specific SFDA regulations and standards.",
                icon: ShieldCheck,
              },
              {
                title: "Safety & Quality Assurance",
                desc: "Verifies manufacturing and handling compliance with national standards.",
                icon: CheckCircle,
              },
              {
                title: "Market Access to Saudi Arabia",
                desc: "Grants access to Saudi Arabia's highly regulated market.",
                icon: Globe,
              },
              {
                title: "Food Safety & Hygiene",
                desc: "Ensures strict compliance with safety and quality standards.",
                icon: Factory,
              },
              {
                title: "Efficacy Verification",
                desc: "Ensures pharmaceuticals and devices meet safety and efficacy standards.",
                icon: HeartPulse,
              },
              {
                title: "GMP Compliance",
                desc: "Adheres to Good Manufacturing Practices standards.",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-amber-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* SFDA Compliance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is SFDA Compliance?
          </h2>
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              SFDA compliance involves ensuring that organizations meet the
              regulations set by the Saudi Food and Drug Authority:
            </p>
            <ul className="space-y-3">
              {[
                "Product Registration - Submitting documentation for SFDA approval",
                "GMP Adherence - Following best practices for manufacturing and handling",
                "Safety & Efficacy Testing - Ensuring products meet quality standards",
                "Labeling & Packaging - Meeting SFDA labeling and packaging regulations",
                "SFDA compliance reduces risk of product recalls, fines, and penalties",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of SFDA Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Market Access to Saudi Arabia's lucrative regulated market",
              "Regulatory Compliance meeting local food and drug regulations",
              "Product Safety ensuring consumer trust and confidence",
              "Global Recognition across Middle Eastern markets",
              "Operational Efficiency reducing waste and recall risks",
              "Brand Credibility enhancing reputation internationally",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of SFDA Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            SFDA certification helps organizations demonstrate:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Commitment to product safety and quality standards",
              "Compliance with Saudi Arabia's strict regulatory standards",
              "Consumer protection and product safety assurance",
              "Regional market recognition and expansion opportunities",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-amber-100 to-yellow-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-amber-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of SFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Access to Saudi Market for product distribution",
              "Increased Consumer Confidence and trust",
              "Regulatory Compliance reducing legal risks",
              "Operational Improvement in production quality",
              "Brand Credibility and market reputation",
              "Regional Market Recognition in Middle East",
              "Reduced Non-Compliance Risks and penalties",
              "Enhanced Product Quality standards",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require SFDA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Food Manufacturing & Processing",
              "Pharmaceutical Manufacturing",
              "Medical Device Manufacturing",
              "Cosmetic Production",
              "Food Packaging & Distribution",
              "Healthcare & Medical Services",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-amber-700 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get SFDA Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Product Registration",
                desc: "Submit products for SFDA approval with all required documentation",
              },
              {
                step: 2,
                title: "Facility Inspections",
                desc: "Undergo inspections for GMP compliance and operational standards",
              },
              {
                step: 3,
                title: "Regulatory Compliance",
                desc: "Implement processes ensuring SFDA compliance for food safety",
              },
              {
                step: 4,
                title: "Pre-market Approval",
                desc: "Obtain pre-market approval for pharmaceuticals and medical devices",
              },
              {
                step: 5,
                title: "Labeling & Packaging",
                desc: "Ensure all products meet SFDA labeling and packaging requirements",
              },
              {
                step: 6,
                title: "Ongoing Monitoring",
                desc: "Maintain records and continuous compliance with SFDA standards",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for SFDA Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert SFDA consultancy services,
              helping businesses navigate the complex regulatory environment of
              Saudi Arabia's food, drug, and medical device sectors.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Product Registration and Documentation",
                "Facility Inspections and GMP Compliance",
                "Pre-market Approval Support",
                "Labeling and Packaging Review",
                "Internal Audits and Pre-Certification",
                "Expert Regulatory Guidance",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-700 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses achieve SFDA compliance, ensuring safe,
              high-quality products for the Saudi Arabian market.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-700" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-amber-700" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default SFDA_Details;
